import apiClient from "repositories/apiClient";
import { getBranches, getServices } from "./reservationRepository";
import { BranchType, ISingleService } from "types";

export type PersonalProfile = {
  id: string;
  firstName: string;
  lastName: string;
  title: string;
  avatarUrl: string | null;
  bio: string | null;
};

type PersonalEndpointOption = {
  optionId: string;
  translations: Record<string, string>;
};

type PersonalEndpointService = {
  serviceId: string;
  translations: Record<string, string>;
  descriptions: Record<string, string>;
  options: PersonalEndpointOption[];
};

type PersonalEndpointTherapist = {
  acceptsNewPatients: boolean;
  id: string;
  branchId: string;
  user: PersonalProfile;
  services: PersonalEndpointService[];
};

type PersonalEndpointResponse = {
  success: boolean;
  result: {
    therapists: PersonalEndpointTherapist[];
  };
};

export type PersonalData = {
  specialist: PersonalProfile;
  services: ISingleService[];
  branches: BranchType[];
  therapists: PersonalEndpointTherapist[];
};

export const getPersonalData = async (customerId: string, userId: string): Promise<PersonalData> => {
  const [personalResponse, servicesResponse, branchesResponse] = await Promise.all([
    apiClient.get<PersonalEndpointResponse>(`public/${customerId}/users/${userId}`),
    getServices(customerId),
    getBranches(customerId),
  ]);

  const therapists = personalResponse.data.result.therapists ?? [];
  const specialist = therapists[0]?.user;

  if (!specialist) {
    throw new Error("Personal reservation specialist was not found");
  }

  const branchIds = new Set(therapists.map(({ branchId }) => branchId));
  const allowedOptionsByService = new Map<string, Set<string>>();

  therapists.forEach((therapist) => {
    therapist.services.forEach((service) => {
      const optionIds = allowedOptionsByService.get(service.serviceId) ?? new Set<string>();
      service.options.forEach(({ optionId }) => optionIds.add(optionId));
      allowedOptionsByService.set(service.serviceId, optionIds);
    });
  });

  const servicesById = new Map<string, ISingleService>();

  (servicesResponse.data.result ?? [])
    .filter((service: ISingleService) => allowedOptionsByService.has(service.id))
    .forEach((service: ISingleService) => {
      const allowedOptionIds = allowedOptionsByService.get(service.id);
      const supportedOptions = service.options.filter((option) => allowedOptionIds?.has(option.id));
      const existingService = servicesById.get(service.id);

      if (!existingService) {
        servicesById.set(service.id, { ...service, options: supportedOptions });
        return;
      }

      const optionsById = new Map(existingService.options.map((option) => [option.id, option]));
      supportedOptions.forEach((option) => optionsById.set(option.id, option));
      servicesById.set(service.id, { ...existingService, options: Array.from(optionsById.values()) });
    });

  const services = Array.from(servicesById.values());

  const branches: BranchType[] = (branchesResponse.data.result ?? []).filter((branch: BranchType) =>
    branchIds.has(branch.id),
  );

  return { specialist, services, branches, therapists };
};
